import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Hero } from "../components/Hero";
import { PlatformTrio } from "../components/PlatformTrio";
import { Marquee } from "../components/Marquee";
import { Ecosystem } from "../components/Ecosystem";
import { FounderPortrait } from "../components/FounderPortrait";
import { Eyebrow, MaskLines, Reveal } from "../components/Reveal";
import { Section } from "../components/PageHeader";
import { useContent, useMeta } from "../lib/ContentContext";

const Word = ({ w, range, progress }) => {
  const color = useTransform(progress, range, ["#7d879a", "#f8fafc"]);
  return <motion.span style={{ color }} className="inline-block mr-[0.25em]">{w}</motion.span>;
};

const Statement = ({ text }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.45"] });
  const words = text.split(" ");
  return (
    <p ref={ref} className="font-display text-3xl sm:text-4xl lg:text-[3.4rem] leading-[1.15] font-semibold tracking-tight max-w-6xl" data-testid="about-statement">
      {words.map((w, i) => <Word key={i} w={w} progress={scrollYProgress} range={[i / words.length, (i + 1) / words.length]} />)}
    </p>
  );
};

export default function Home() {
  const { content } = useContent();
  useMeta("/");
  const f = content.founder;
  return (
    <>
      <Hero />
      <Section testid="home-platforms" className="!pt-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <Eyebrow className="mb-6">Our companies & platforms</Eyebrow>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
              <MaskLines lines={["Three platforms.", <span key="o" className="text-gold">One empire.</span>]} />
            </h2>
          </div>
          <Link to="/platforms" className="inline-flex items-center gap-2 text-sm text-[#f3d77a] hover:text-white transition-colors" data-testid="home-all-platforms-link">
            View all platforms <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <PlatformTrio />
      </Section>

      <Marquee />

      <Section testid="home-about">
        <Eyebrow className="mb-10">About BOSS Empire</Eyebrow>
        <Statement text={content.about.statement} />
        <Reveal className="mt-12">
          <Link to="/about" className="inline-flex items-center gap-2 text-sm text-[#f3d77a] border-b border-[#d4af37]/40 pb-1 hover:text-white hover:border-white transition-colors" data-testid="home-about-link">
            More about the company <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </Section>

      <Section id="ecosystem" testid="home-ecosystem" className="bg-[#05060a] border-y border-white/5">
        <Eyebrow className="mb-6">Interactive ecosystem</Eyebrow>
        <h2 className="font-display font-bold text-4xl sm:text-5xl uppercase mb-14 max-w-3xl leading-[0.95]">
          <MaskLines lines={["Connected by", <span key="d" className="font-serif-i normal-case text-[#f3e5ab] font-normal">design.</span>]} />
        </h2>
        <Ecosystem />
      </Section>

      <Section testid="home-founder">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5"><FounderPortrait alt={`${f.name} (${f.alias}), ${f.title}`} /></div>
          <div className="lg:col-span-7">
            <Eyebrow className="mb-8">Founder & CEO</Eyebrow>
            <h2 className="font-display font-extrabold uppercase leading-[0.9] text-5xl sm:text-6xl lg:text-7xl">
              <MaskLines lines={[f.name.split(" ")[0], <span key="l" className="text-gold">{f.name.split(" ").slice(1).join(" ")}</span>]} />
            </h2>
            <Reveal><p className="font-serif-i text-2xl text-[#f3e5ab] mt-6">“{f.alias}”</p></Reveal>
            <Reveal delay={0.1}><p className="text-slate-300 leading-relaxed mt-8 max-w-xl">{f.intro}</p></Reveal>
            <Reveal delay={0.2}>
              <Link to="/founder/zahir-patel" className="btn-gold mt-10 inline-flex items-center gap-2 rounded-full px-7 py-4 min-h-[52px] text-sm font-semibold" data-testid="home-founder-link">
                Founder & Leadership <ArrowUpRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </Section>

      <Section testid="home-vision-cta" className="!pt-0">
        <Reveal>
          <div className="relative overflow-hidden rounded-[32px] border border-[#0066ff]/30 bg-gradient-to-br from-[#06102a] via-[#07080d] to-[#07080d] p-8 sm:p-14 lg:p-20">
            <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#00f0ff]/15 blur-[100px]" />
            <p className="eyebrow mb-6">Vision & Innovation</p>
            <p className="relative font-display text-3xl sm:text-5xl font-bold max-w-4xl leading-tight">{content.vision.statement.split("—")[0]}</p>
            <div className="relative mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/vision" className="btn-gold inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 min-h-[52px] text-sm font-semibold" data-testid="home-vision-link">Discover Our Vision <ArrowUpRight className="w-4 h-4" /></Link>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 min-h-[52px] text-sm border border-white/20 hover:border-[#00f0ff] hover:text-[#00f0ff] transition-colors" data-testid="home-contact-link">Contact Us</Link>
            </div>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
